import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";
import { sendInvitationSetupEmail } from "./authService";
import { buildInvitationPasswordSetupUrl } from "../local/invitationLinks";

export const STAFF_CHANGE_TYPES = Object.freeze({
  CREATE: "create",
  UPDATE: "update",
  DEACTIVATE: "deactivate",
  REACTIVATE: "reactivate",
});

export const STAFF_CHANGE_STATUSES = Object.freeze({
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
});

const REQUEST_STORAGE_PREFIX = "mozzoapp:staff-change-requests:v1";
const STATUS_STORAGE_PREFIX = "mozzoapp:staff-operational-status:v1";

const getStorageKey = (prefix, companyId) => `${prefix}:${companyId || "local"}`;

const createLocalId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `staff-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const readLocalJson = (key, fallback) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeLocalJson = (key, value) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(value));
};

const normalizeRequest = (row = {}) => ({
  id: row.id || "",
  companyId: row.company_id || row.companyId || "",
  branchId: row.branch_id || row.branchId || "",
  requestedBy: row.requested_by || row.requestedBy || "",
  requestedByName: row.requested_by_name || row.requestedByName || "",
  requestType: row.request_type || row.requestType || STAFF_CHANGE_TYPES.CREATE,
  targetUserId: row.target_user_id || row.targetUserId || "",
  payload: row.payload && typeof row.payload === "object" ? row.payload : {},
  reason: row.reason || "",
  status: row.status || STAFF_CHANGE_STATUSES.PENDING,
  reviewedBy: row.reviewed_by || row.reviewedBy || "",
  reviewNote: row.review_note || row.reviewNote || "",
  reviewedAt: row.reviewed_at || row.reviewedAt || "",
  createdAt: row.created_at || row.createdAt || new Date().toISOString(),
});

const isMissingStaffManagementSchema = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("branch_staff_change_requests") ||
    message.includes("review_branch_staff_change_request") ||
    message.includes("set_branch_operational_staff_active")
  );
};

const getLocalRequests = (companyId) => {
  const value = readLocalJson(getStorageKey(REQUEST_STORAGE_PREFIX, companyId), []);
  return (Array.isArray(value) ? value : []).map(normalizeRequest);
};

const saveLocalRequests = (companyId, requests) =>
  writeLocalJson(getStorageKey(REQUEST_STORAGE_PREFIX, companyId), requests);

export const listBranchPersonnel = async (branchId, { localOnly = false } = {}) => {
  if (!branchId || localOnly || !isSupabaseConfigured || !supabase) {
    return { users: [], storage: "local" };
  }

  const { data, error } = await supabase.rpc("list_branch_personnel", {
    p_branch_id: branchId,
  });

  if (!error) {
    return {
      users: (data || []).map((row) => ({
        user_id: row.user_id,
        role: "admin_limitado",
        workspace_role: row.workspace_role || "admin_limitado",
        active: row.active !== false && row.branch_active !== false,
        profileFullName: row.full_name || "",
        profilePhone: row.phone || "",
        primaryBranchId: row.branch_id || branchId,
        primaryBranchName: row.branch_name || "Sucursal",
        branchAccesses: [
          {
            branchId: row.branch_id || branchId,
            branchName: row.branch_name || "Sucursal",
            active: row.branch_active !== false,
            permissions: {
              canViewMenu: row.can_view_menu === true,
              canManageMenu: row.can_manage_menu === true,
              canViewStock: row.can_view_stock === true,
              canManageStock: row.can_manage_stock === true,
              canManageOrders: row.can_manage_orders === true,
              canManageQr: row.can_manage_qr === true,
              canManagePayments: row.can_manage_payments === true,
              canManageSettings: row.can_manage_settings === true,
              waiterCanTakeOrders: row.waiter_can_take_orders === true,
              waiterCanModifyOrders: row.waiter_can_modify_orders === true,
              waiterCanCancelOrders: row.waiter_can_cancel_orders === true,
              waiterCanChargeOrders: row.waiter_can_charge_orders === true,
              waiterCanRefundOrders: row.waiter_can_refund_orders === true,
            },
          },
        ],
      })),
      storage: "remote",
    };
  }

  if (isMissingStaffManagementSchema(error)) {
    return { users: [], storage: "local" };
  }

  throw new Error(error.message || "No se pudo cargar el personal de la sucursal.");
};

export const listPersonnelChangeRequests = async (
  companyId,
  { branchId = "", localOnly = false } = {}
) => {
  if (!companyId) {
    return { requests: [], storage: "local" };
  }

  if (!localOnly && isSupabaseConfigured && supabase) {
    let query = supabase
      .from("branch_staff_change_requests")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });

    if (branchId) {
      query = query.eq("branch_id", branchId);
    }

    const { data, error } = await query;

    if (!error) {
      return {
        requests: (data || []).map(normalizeRequest),
        storage: "remote",
      };
    }

    if (!isMissingStaffManagementSchema(error)) {
      throw new Error(error.message || "No se pudieron cargar las solicitudes de personal.");
    }
  }

  const requests = getLocalRequests(companyId).filter(
    (request) => !branchId || request.branchId === branchId
  );

  return { requests, storage: "local" };
};

export const createPersonnelChangeRequest = async ({
  companyId,
  branchId,
  requestedBy,
  requestedByName = "",
  requestType,
  targetUserId = "",
  payload = {},
  reason = "",
  localOnly = false,
} = {}) => {
  if (!companyId || !branchId || !requestedBy || !requestType) {
    throw new Error("Faltan datos para registrar la solicitud de personal.");
  }

  const row = {
    company_id: companyId,
    branch_id: branchId,
    requested_by: requestedBy,
    requested_by_name: requestedByName || null,
    request_type: requestType,
    target_user_id: targetUserId || null,
    payload,
    reason: reason.trim() || null,
  };

  if (!localOnly && isSupabaseConfigured && supabase) {
    const { data, error } = await supabase
      .from("branch_staff_change_requests")
      .insert(row)
      .select("*")
      .single();

    if (!error) {
      return { request: normalizeRequest(data), storage: "remote" };
    }

    if (!isMissingStaffManagementSchema(error)) {
      throw new Error(error.message || "No se pudo enviar la solicitud de personal.");
    }
  }

  const nextRequest = normalizeRequest({
    id: createLocalId(),
    companyId,
    branchId,
    requestedBy,
    requestedByName,
    requestType,
    targetUserId,
    payload,
    reason,
    status: STAFF_CHANGE_STATUSES.PENDING,
  });
  const requests = getLocalRequests(companyId);
  saveLocalRequests(companyId, [nextRequest, ...requests]);

  return { request: nextRequest, storage: "local" };
};

export const reviewPersonnelChangeRequest = async ({
  companyId,
  requestId,
  decision,
  reviewedBy,
  reviewNote = "",
  localOnly = false,
} = {}) => {
  if (!companyId || !requestId || !reviewedBy) {
    throw new Error("No encontramos la solicitud de personal que quieres revisar.");
  }

  const status =
    decision === STAFF_CHANGE_STATUSES.APPROVED
      ? STAFF_CHANGE_STATUSES.APPROVED
      : STAFF_CHANGE_STATUSES.REJECTED;

  if (!localOnly && isSupabaseConfigured && supabase) {
    const { data, error } = await supabase.rpc("review_branch_staff_change_request", {
      p_request_id: requestId,
      p_decision: status,
      p_review_note: reviewNote.trim() || null,
    });

    if (!error) {
      return {
        request: normalizeRequest(data),
        storage: "remote",
        applied: status === STAFF_CHANGE_STATUSES.APPROVED,
        invitation: data?.invitation || null,
      };
    }

    if (!isMissingStaffManagementSchema(error)) {
      throw new Error(error.message || "No se pudo revisar la solicitud de personal.");
    }
  }

  const requests = getLocalRequests(companyId);
  const index = requests.findIndex((request) => request.id === requestId);

  if (index < 0) {
    throw new Error("La solicitud ya no esta disponible en este navegador.");
  }

  const nextRequest = normalizeRequest({
    ...requests[index],
    status,
    reviewedBy,
    reviewNote,
    reviewedAt: new Date().toISOString(),
  });
  const nextRequests = [...requests];
  nextRequests[index] = nextRequest;
  saveLocalRequests(companyId, nextRequests);

  return { request: nextRequest, storage: "local", applied: false };
};

export const deliverApprovedPersonnelInvitation = async (invitation) => {
  if (!invitation?.email || !invitation?.invitation_token || typeof window === "undefined") {
    return { sent: false, message: "La invitacion fue creada sin datos suficientes para enviar el correo." };
  }

  try {
    const baseUrl = window.location.origin.replace(/\/$/, "");
    await sendInvitationSetupEmail({
      email: invitation.email,
      redirectTo: buildInvitationPasswordSetupUrl(invitation, baseUrl),
      userData: {
        invitation_token: invitation.invitation_token,
        company_id: invitation.company_id || "",
        branch_id: invitation.branch_id || "",
        workspace_role: invitation.workspace_role || "",
      },
    });
    return { sent: true, message: "Invitacion aprobada y enviada al integrante." };
  } catch (error) {
    return {
      sent: false,
      message:
        error.message ||
        "La invitacion fue aprobada, pero no se pudo enviar el correo de acceso.",
    };
  }
};

export const getLocalOperationalStaffStates = (companyId) => {
  const value = readLocalJson(getStorageKey(STATUS_STORAGE_PREFIX, companyId), {});
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};

export const setOperationalStaffActive = async ({
  companyId,
  branchId,
  targetUserId,
  active,
  localOnly = false,
} = {}) => {
  if (!companyId || !branchId || !targetUserId) {
    throw new Error("No encontramos el integrante operativo que quieres actualizar.");
  }

  if (!localOnly && isSupabaseConfigured && supabase) {
    const { data, error } = await supabase.rpc("set_branch_operational_staff_active", {
      p_branch_id: branchId,
      p_target_user_id: targetUserId,
      p_active: active === true,
    });

    if (!error) {
      return { result: data || null, storage: "remote" };
    }

    if (!isMissingStaffManagementSchema(error)) {
      throw new Error(error.message || "No se pudo actualizar el estado del personal.");
    }
  }

  const states = getLocalOperationalStaffStates(companyId);
  const key = `${branchId}:${targetUserId}`;
  const nextStates = {
    ...states,
    [key]: active === true,
  };
  writeLocalJson(getStorageKey(STATUS_STORAGE_PREFIX, companyId), nextStates);

  return { result: { active: active === true }, storage: "local" };
};
